/**
 * Shared time-unit helpers for the Fraud Detection screens.
 *
 * A fraud rule stores its evaluation window in seconds (e.g. 3600 → 1 hour).
 * The Rules table renders that as `"N unit(s)"` using the coarsest unit that
 * divides the window evenly — e.g. 3600 → "1 hour(s)", 45 → "45 second(s)".
 * Flagged users are evaluated against a rule and we need to display the
 * time-to-trigger value in the SAME unit as the rule's period, so operators
 * can compare the two side-by-side without converting in their head.
 */

export type FraudTimeUnit = "second" | "minute" | "hour" | "day";

const SECONDS_PER_UNIT: Record<FraudTimeUnit, number> = {
  second: 1,
  minute: 60,
  hour: 3600,
  day: 86400,
};

/**
 * Pick the coarsest unit that expresses the given seconds without truncation.
 * Falls back to `"second"` for missing/invalid input. Keep this rule in sync
 * with {@link formatEvaluationWindowLabel}.
 */
export function resolveFraudRuleTimeUnit(
  evaluationWindowSeconds: number | null | undefined,
): FraudTimeUnit {
  const s = Number(evaluationWindowSeconds);
  if (!Number.isFinite(s) || s <= 0) return "second";
  if (s >= 86400 && s % 86400 === 0) return "day";
  if (s >= 3600 && s % 3600 === 0) return "hour";
  if (s >= 60 && s % 60 === 0) return "minute";
  return "second";
}

/**
 * Renders a seconds value as a `"N unit(s)"` string, coercing to the coarsest
 * unit that divides evenly. Used for a rule's own period label.
 */
export function formatEvaluationWindowLabel(seconds: number): string {
  if (!Number.isFinite(seconds)) return String(seconds);
  const unit = resolveFraudRuleTimeUnit(seconds);
  const value = seconds / SECONDS_PER_UNIT[unit];
  return `${value} ${unit}(s)`;
}

/**
 * Renders a seconds value in a SPECIFIC unit (typically the unit of the rule
 * that triggered the flag). Fractional conversions are rounded to 2 decimals
 * so e.g. 90 seconds shown in minutes is `"1.5 minute(s)"`.
 *
 * Returns `"—"` for null/undefined/non-numeric input so callers can hand the
 * value straight to a table cell.
 */
export function formatSecondsAsFraudDuration(
  seconds: number | null | undefined,
  unit: FraudTimeUnit,
): string {
  if (seconds == null) return "—";
  const s = Number(seconds);
  if (!Number.isFinite(s)) return "—";
  const raw = s / SECONDS_PER_UNIT[unit];
  const rounded = Number.isInteger(raw) ? raw : Math.round(raw * 100) / 100;
  return `${rounded} ${unit}(s)`;
}
